import {View} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import React from 'react';
import {ToolsLayerView} from '@packages/systems/ui/shared-ui/ToolsLayerView';
import {useFrameAccent} from './FrameChromeContext';

type FrameResizeHandleProps = {
  /**
   * The dimension of the frame that this handle controls
   */
  dimension: 'width' | 'height';
  /**
   * Callback for when the handle is dragged.
   * The delta amount is in screen pixels, and represents the change in the frame's size.
   * The `eventType` provides an indication of whether this is the first move event (`drag-start`) or a subsequent move event (`drag`).
   */
  onDrag: OnResizeFn;
  /**
   * Callback for when the drag is finished and the mouse is released.
   */
  onDragEnd: (dimension: 'width' | 'height') => void;
  /**
   * Callback for when the handle is double clicked
   */
  onDoubleClick: (dimension: 'width' | 'height') => void;
};

export const FrameResizeHandle = ({
  dimension,
  onDrag,
  onDragEnd,
  onDoubleClick,
}: FrameResizeHandleProps) => {
  const accent = useFrameAccent();

  const previousPointerPosition = React.useRef(0);
  const isMouseDownRef = React.useRef(false);
  const isDragging = React.useRef(false);
  const eventType = React.useRef<'drag-start' | 'drag' | undefined>(undefined);

  const [isDragActive, setIsDragActive] = React.useState(false);

  const getCurrentPointerPosition = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      return dimension === 'width' ? e.clientX : e.clientY;
    },
    [dimension]
  );

  const handleMouseDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;

      const target: HTMLDivElement = e.target as HTMLDivElement;
      target.setPointerCapture(e.pointerId);

      previousPointerPosition.current = getCurrentPointerPosition(e);

      isMouseDownRef.current = true;
      eventType.current = 'drag-start';
    },
    [getCurrentPointerPosition]
  );

  const handleMouseUp = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isMouseDownRef.current) return;

      if (isDragging.current) {
        onDragEnd(dimension);
      }

      isDragging.current = false;
      isMouseDownRef.current = false;

      const target: HTMLDivElement = e.target as HTMLDivElement;
      target.releasePointerCapture(e.pointerId);

      setIsDragActive(false);
      eventType.current = undefined;
    },
    [dimension, onDragEnd]
  );

  const handleMouseMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (isMouseDownRef.current) {
        setIsDragActive(true);

        const currentPointerPosition = getCurrentPointerPosition(e);
        const delta = currentPointerPosition - previousPointerPosition.current;

        const sign = Math.sign(delta);
        const absAmount = Math.abs(delta);
        const roundedAmount = Math.floor(absAmount);

        if (roundedAmount < 1) return;

        const actualDragDelta = sign * roundedAmount;

        previousPointerPosition.current += actualDragDelta;
        isDragging.current = true;
        onDrag(actualDragDelta, dimension, eventType.current);

        // Only include `drag-start` with the initial drag event
        if (eventType.current === 'drag-start') {
          eventType.current = 'drag';
        }
      }
    },
    [getCurrentPointerPosition, onDrag, dimension]
  );

  const handleDoubleClick = React.useCallback(() => {
    onDoubleClick(dimension);
  }, [onDoubleClick, dimension]);

  return (
    <HandleDraggerArea
      data-automation-id={`frame-${dimension}-handle-dragger`}
      dimension={dimension}
      accent={accent}
      data-is-drag-active={isDragActive}
      onPointerDown={handleMouseDown}
      onPointerUp={handleMouseUp}
      onPointerMove={handleMouseMove}
      onPointerCancel={handleMouseUp}
      onLostPointerCapture={handleMouseUp}
      onDoubleClick={handleDoubleClick}
    >
      <HandleDraggerLineWrapper
        data-automation-id={`frame-${dimension}-handle-dragger-line-wrapper`}
        dimension={dimension}
      >
        <HandleDraggerLine accent={accent} />
      </HandleDraggerLineWrapper>
    </HandleDraggerArea>
  );
};

const HandleDraggerLine = emotionStyled(View)(
  {
    borderRadius: '$1',
    height: '100%',
    width: '100%',
  },
  {
    variants: {
      accent: {
        green: {
          border: '1px solid $greenCanvas',
        },
        blue: {
          border: '1px solid $blueCanvas',
        },
      },
    },
    defaultVariants: {
      accent: 'blue',
    },
  }
);

const HandleDraggerLineWrapper = emotionStyled(View)(
  {
    boxSizing: 'content-box',
    pointerEvents: 'none',
  },
  {
    variants: {
      dimension: {
        width: {
          // Sets the minimum height of the handle to 48px, and allows it to grow up to 10% of the tools container height.
          height: 'max(48px, 10cqh)',
          transform: 'translateX(-5px)',
          padding: '0 8px',
        },
        height: {
          // Sets the minimum width of the handle to 48px, and allows it to grow up to 10% of the tools container width.
          width: 'max(48px, 10cqw)',
          transform: 'translateY(-5px)',
          padding: '8px 0',
        },
      },
    },
  }
);

const HandleDraggerArea = emotionStyled(ToolsLayerView)(
  {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
  },
  {
    variants: {
      accent: {
        green: {
          '&:hover': {
            backgroundColor: '$greenTransparent',
          },
        },
        blue: {
          '&:hover': {
            backgroundColor: '$blueTransparent',
          },
        },
      },
      dimension: {
        width: {
          right: 0,
          flexDirection: 'column',
          width: '8px',
          height: '100%',
          transform: 'translateX(100%)',
          cursor: 'ew-resize',
        },
        height: {
          bottom: 0,
          flexDirection: 'row',
          width: '100%',
          height: '8px',
          transform: 'translateY(100%)',
          cursor: 'ns-resize',
        },
      },
    },
    defaultVariants: {
      accent: 'blue',
    },
  }
);

export type OnResizeFn = (
  delta: number,
  dimension: 'width' | 'height',
  eventType: 'drag-start' | 'drag' | undefined
) => void;

type UseFrameResizeEventHandlersArgs = {
  onResized: OnResizeFn;
};

type UseFrameResizeEventHandlersHook = (
  args: UseFrameResizeEventHandlersArgs
) => {
  onFrameHandleDrag: OnResizeFn;
};

/**
 * A helper hook to be used with `FrameResizeHandle`.
 *
 * The hook takes an `onResized` to be called with the delta values as the resize handle is being dragged,
 * and returns an `onHandleDrag` to be attached to the `FrameResizeHandle`'s `onDrag` prop.
 *
 * Under the hood, the hook takes care of throttling the `onResized` calls.
 */
export const useFrameResizeEventHandlers: UseFrameResizeEventHandlersHook = ({
  onResized,
}) => {
  const deltaAccumulator = React.useRef(0);
  const rafIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  const scheduleDispatch = React.useCallback(
    (dimension: 'width' | 'height', eventType?: 'drag-start' | 'drag') => {
      // If we already have a scheduled `requestAnimationFrame`, we don't need to schedule another one
      if (rafIdRef.current) {
        return;
      }

      rafIdRef.current = requestAnimationFrame(() => {
        if (deltaAccumulator.current !== 0) {
          onResized(deltaAccumulator.current, dimension, eventType);
        }

        rafIdRef.current = null;
        deltaAccumulator.current = 0;
      });
    },
    [onResized]
  );

  const onFrameHandleDrag: OnResizeFn = React.useCallback(
    (delta, dimension, eventType) => {
      deltaAccumulator.current += delta;
      scheduleDispatch(dimension, eventType);
    },
    [scheduleDispatch]
  );

  return {
    onFrameHandleDrag,
  };
};
